import React from 'react';

import { formatLinkBlockToUrl } from '$utilities/format.js';
import { IconArrowRight } from '$partials/icon/index.js';
import { isLocalURL } from '$utilities/browser';
import { Map } from '$partials/ui/index.js';
import { PageShell } from '$partials/global/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').ContactPage}
 */
const ContactPage = (props) => {
    const page = props.data?.page;

    const openingHours = page?.openingHours?.filter(Boolean) || [];

    const ctaHref = formatLinkBlockToUrl(page?.ctaLink);
    const isCtaExternal = ctaHref && !isLocalURL(ctaHref);

    const mapLabel = page?.directionsHeading || page?.title || '';

    return (
        <PageShell
            footer={props.data?.footer}
            header={props.data?.header}>
            <div className='layout-container bg-neutral-00'>
                <div className='py-16 sm:py-40'>
                    <div className='layout-grid flex-col sm:flex-row gap-y-16 sm:gap-y-0'>
                        {/* Page header */}
                        <div className='w-full sm:w-4/12 md:w-3/12 lg:w-3/12 layout-grid-col'>
                            <div className='sm:sticky sm:top-110'>
                                <h1 className='type-style-heading'>
                                    {page?.title}
                                </h1>

                                {page?.introduction && (
                                    <div
                                        className='rich-text type-style-heading'
                                        dangerouslySetInnerHTML={{ __html: page.introduction }} />
                                )}

                                {page?.contactDetails && (
                                    <div
                                        className='rich-text type-style-body mt-24 sm:mt-48'
                                        dangerouslySetInnerHTML={{ __html: page.contactDetails }} />
                                )}

                                {(page?.ctaLabel && ctaHref) && (
                                    <p className='mt-16 sm:mt-26'>
                                        <a
                                            className='button type-style-body cursor-pointer'
                                            href={ctaHref}
                                            rel={isCtaExternal ? 'noopener noreferrer' : undefined}
                                            target={isCtaExternal ? '_blank' : undefined}>
                                            {page.ctaLabel}

                                            <span
                                                aria-hidden={true}
                                                className='ml-5'>
                                                <IconArrowRight
                                                    height={20}
                                                    width={20} />
                                            </span>
                                        </a>
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className='w-full sm:w-8/12 md:w-9/12'>
                            {/* Push heading down */}
                            <div
                                aria-hidden={true}
                                className='hidden sm:block sm:w-4/8 md:w-4/9 lg:w-3/8 layout-grid-col text-transparent type-style-heading select-none pointer-events-none'>
                                {page?.title}
                            </div>

                            {/* Getting here */}
                            {(page?.directionsHeading || page?.directionsText) && (
                                <div>
                                    {page?.directionsHeading && (
                                        <h2 className='type-style-heading sm:w-5/8 md:w-5/9 layout-grid-col'>{page.directionsHeading}</h2>
                                    )}

                                    {page?.directionsText && (
                                        <div
                                            className='rich-text type-style-body mt-8 sm:mt-10 sm:w-4/9 layout-grid-col'
                                            dangerouslySetInnerHTML={{ __html: page.directionsText }} />
                                    )}
                                </div>
                            )}

                            <div className='layout-grid-col mt-24 sm:mt-32'>
                                <Map
                                    label={mapLabel}
                                    latitude={page?.latitude}
                                    longitude={page?.longitude}
                                    zoom={page?.zoom} />
                            </div>

                            {/* Opening hours */}
                            {(page?.openingHoursHeading || page?.openingHoursText || openingHours.length > 0) && (
                                <div className='sm:w-4/8 md:w-4/9 layout-grid-col mt-24 sm:mt-32'>
                                    {page?.openingHoursHeading && (
                                        <h2 className='type-style-heading'>{page.openingHoursHeading}</h2>
                                    )}

                                    {page?.openingHoursText && (
                                        <div
                                            className='rich-text type-style-body mt-8 sm:mt-10'
                                            dangerouslySetInnerHTML={{ __html: page.openingHoursText }} />
                                    )}

                                    {openingHours.length > 0 && (
                                        <table className='w-full type-style-body mt-10 sm:mt-16'>
                                            <caption className='sr-only'>
                                                {page?.openingHoursHeading || 'Opening hours'}
                                            </caption>

                                            <tbody>
                                                {openingHours.map((row) => (
                                                    <tr key={row.id}>
                                                        <th
                                                            className='w-1/2 py-0 pl-0 pr-8 font-normal text-left'
                                                            scope='row'>
                                                            {row.label}
                                                        </th>

                                                        <td className='py-0 pl-8 pr-0'>{row.value}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </PageShell>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default ContactPage;
